import { useEffect, useRef } from 'react';
import { Container, Grid, Typography, makeStyles } from '@material-ui/core';
import Video from 'twilio-video';
import { Loading } from '../../../icons/Loading';
import { ActivePane, useAppStateContext } from '../../AppStateProvider/AppStateProvider';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  textBox: {
    width: '340px',
    height: '48px',
    textAlign: 'center',
  },
  loading: {
    animation: '$loading 1.5s linear infinite',
    display: 'flex',
  },
  '@keyframes loading': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
});

/**
 * The single background-checks step. After the user finishes the audio test we
 * run the remaining checks (browser support, connectivity/preflight, and
 * bitrate/quality) behind one spinner instead of walking the user through a
 * separate pane for each. When everything has finished we automatically hand
 * off to the consolidated Results pane, so the user never has to click through
 * intermediate screens.
 *
 * The preflight and bitrate tests are actually kicked off much earlier (when
 * the user leaves the GetStarted pane), so by the time they reach this pane the
 * checks are often already complete and the spinner is only shown briefly.
 */
export function RunningChecks() {
  const { state, dispatch } = useAppStateContext();
  const classes = useStyles();
  const advanced = useRef(false);

  // An unsupported browser is a hard failure — WebRTC won't work at all — so we
  // don't need to wait for the connectivity / quality checks to advance.
  const browserUnsupported = !Video.isSupported;
  const testsFinished = state.preflightTestFinished && state.bitrateTestFinished;

  useEffect(() => {
    // Every pane is mounted up-front by MainContent (panes are only visually
    // hidden), so we must gate on the user actually being on this pane before
    // auto-advancing — otherwise we would jump to Results on initial load.
    if (state.activePane !== ActivePane.RunningChecks) return;
    if (advanced.current) return;

    if (browserUnsupported || testsFinished) {
      advanced.current = true;
      dispatch({ type: 'set-active-pane', newActivePane: ActivePane.Results });
    }
  }, [state.activePane, browserUnsupported, testsFinished, dispatch]);

  return (
    <Container>
      <Grid container className={classes.container}>
        <Typography variant="h1" gutterBottom>
          Hang tight!
        </Typography>
        <Typography variant="body1" gutterBottom className={classes.textBox}>
          We're just finishing up your personalized results and creating your report.
        </Typography>
        <div className={classes.loading}>
          <Loading />
        </div>
      </Grid>
    </Container>
  );
}
